import { ColumnInfo } from './dataAnalyzer';

export interface KPI {
  id: string;
  title: string;
  value: string | number;
  formattedValue: string;
  type: 'count' | 'sum' | 'average' | 'percentage' | 'ratio' | 'growth' | 'range';
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  icon: string;
  description: string;
  category: 'volume' | 'performance' | 'quality' | 'distribution' | 'financial';
  priority: number;
  color: string;
}

export class KPIAnalyzer {
  private data: any[];
  private headers: string[];
  private columnInfo: Map<string, ColumnInfo>;

  constructor(data: any[], headers: string[], columnInfo: Map<string, ColumnInfo>) {
    this.data = data;
    this.headers = headers;
    this.columnInfo = columnInfo;
  }

  generateKPIs(): KPI[] {
    const kpis: KPI[] = [];

    // Add basic business metrics (remove technical data dimensions)
    kpis.push(...this.generateBasicMetrics());
    
    // Add numeric column insights
    kpis.push(...this.generateNumericKPIs());
    
    // Add categorical insights
    kpis.push(...this.generateCategoricalKPIs());
    
    // Add time-based insights if available
    kpis.push(...this.generateTimeBasedKPIs());
    
    // Add data quality metrics (only if really relevant)
    kpis.push(...this.generateQualityKPIs());

    // Sort by priority and return exactly top 4 for consistent layout
    return kpis
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 4);
  }

  private generateBasicMetrics(): KPI[] {
    const kpis: KPI[] = [];

    // Only include total records if it's business meaningful
    // Use more business-friendly terminology
    const recordTitle = this.getRecordTitle();
    
    kpis.push({
      id: 'total-records',
      title: recordTitle,
      value: this.data.length,
      formattedValue: this.formatNumber(this.data.length),
      type: 'count',
      icon: this.getRecordIcon(),
      description: `Total number of ${recordTitle.toLowerCase()}`,
      category: 'volume',
      priority: 95,
      color: '#6366f1'
    });

    return kpis;
  }

  private getRecordTitle(): string {
    // Try to infer what the records represent based on column names
    const headers = this.headers.map(h => h.toLowerCase());
    
    if (headers.some(h => h.includes('customer') || h.includes('client'))) return 'Total Customers';
    if (headers.some(h => h.includes('order') || h.includes('purchase'))) return 'Total Orders';
    if (headers.some(h => h.includes('product') || h.includes('item'))) return 'Total Products';
    if (headers.some(h => h.includes('employee') || h.includes('staff'))) return 'Total Employees';
    if (headers.some(h => h.includes('sale') || h.includes('transaction'))) return 'Total Sales';
    if (headers.some(h => h.includes('lead') || h.includes('prospect'))) return 'Total Leads';
    if (headers.some(h => h.includes('project') || h.includes('task'))) return 'Total Projects';
    if (headers.some(h => h.includes('ticket') || h.includes('issue'))) return 'Total Tickets';
    if (headers.some(h => h.includes('campaign') || h.includes('ad'))) return 'Total Campaigns';
    if (headers.some(h => h.includes('invoice') || h.includes('payment'))) return 'Total Invoices';
    
    return 'Total Records';
  }

  private getRecordIcon(): string {
    const headers = this.headers.map(h => h.toLowerCase());
    
    if (headers.some(h => h.includes('customer') || h.includes('client'))) return '👥';
    if (headers.some(h => h.includes('order') || h.includes('purchase'))) return '🛒';
    if (headers.some(h => h.includes('product') || h.includes('item'))) return '📦';
    if (headers.some(h => h.includes('employee') || h.includes('staff'))) return '👨‍💼';
    if (headers.some(h => h.includes('sale') || h.includes('transaction'))) return '💰';
    if (headers.some(h => h.includes('lead') || h.includes('prospect'))) return '🎯';
    if (headers.some(h => h.includes('project') || h.includes('task'))) return '📋';
    if (headers.some(h => h.includes('ticket') || h.includes('issue'))) return '🎫';
    if (headers.some(h => h.includes('campaign') || h.includes('ad'))) return '📢';
    if (headers.some(h => h.includes('invoice') || h.includes('payment'))) return '💳';
    
    return '📊';
  }

  private generateNumericKPIs(): KPI[] {
    const kpis: KPI[] = [];
    const numericColumns = this.getNumericColumns();

    if (numericColumns.length === 0) return kpis;

    // Find the most important numeric column (highest variance or business relevance)
    const primaryNumericColumn = this.findPrimaryNumericColumn(numericColumns);
    
    if (primaryNumericColumn) {
      const values = this.getNumericValues(primaryNumericColumn);
      const columnTitle = this.formatColumnName(primaryNumericColumn);

      // Total/Sum
      const sum = values.reduce((a, b) => a + b, 0);
      kpis.push({
        id: `total-${primaryNumericColumn}`,
        title: `Total ${columnTitle}`,
        value: sum,
        formattedValue: this.formatNumber(sum),
        type: 'sum',
        icon: this.getNumericIcon(primaryNumericColumn),
        description: `Sum of all ${columnTitle.toLowerCase()} values`,
        category: this.getNumericCategory(primaryNumericColumn),
        priority: 90,
        color: '#10b981'
      });

      // Average
      const average = sum / values.length;
      kpis.push({
        id: `avg-${primaryNumericColumn}`,
        title: `Average ${columnTitle}`,
        value: average,
        formattedValue: this.formatNumber(average),
        type: 'average',
        icon: '📈',
        description: `Mean ${columnTitle.toLowerCase()} per record`,
        category: 'performance',
        priority: 80,
        color: '#f59e0b'
      });

      // Range (Max - Min)
      const max = Math.max(...values);
      const min = Math.min(...values);
      const range = max - min;
      
      kpis.push({
        id: `range-${primaryNumericColumn}`,
        title: `${columnTitle} Range`,
        value: range,
        formattedValue: `${this.formatNumber(min)} - ${this.formatNumber(max)}`,
        type: 'range',
        icon: '📏',
        description: `Range from minimum to maximum ${columnTitle.toLowerCase()}`,
        category: 'distribution',
        priority: 70,
        color: '#06b6d4'
      });
    }

    // Secondary numeric column insights
    if (numericColumns.length > 1) {
      const secondaryColumn = numericColumns.find(col => col !== primaryNumericColumn);
      if (secondaryColumn) {
        const values = this.getNumericValues(secondaryColumn);
        const average = values.reduce((a, b) => a + b, 0) / values.length;
        const columnTitle = this.formatColumnName(secondaryColumn);

        kpis.push({
          id: `avg-${secondaryColumn}`,
          title: `Average ${columnTitle}`,
          value: average,
          formattedValue: this.formatNumber(average),
          type: 'average',
          icon: this.getNumericIcon(secondaryColumn),
          description: `Mean ${columnTitle.toLowerCase()} across all records`,
          category: 'performance',
          priority: 75,
          color: '#8b5cf6'
        });
      }
    }

    return kpis;
  }

  private generateCategoricalKPIs(): KPI[] {
    const kpis: KPI[] = [];
    const categoricalColumns = this.getCategoricalColumns();

    if (categoricalColumns.length === 0) return kpis;

    // Find most diverse categorical column
    const primaryCategoricalColumn = this.findPrimaryCategoricalColumn(categoricalColumns);
    
    if (primaryCategoricalColumn) {
      const counts = this.getCategoryCounts(primaryCategoricalColumn);
      const totalCategories = Object.keys(counts).length;
      const columnTitle = this.formatColumnName(primaryCategoricalColumn);

      // Unique categories count
      kpis.push({
        id: `categories-${primaryCategoricalColumn}`,
        title: `${columnTitle} Types`,
        value: totalCategories,
        formattedValue: `${totalCategories} unique`,
        type: 'count',
        icon: '🏷️',
        description: `Number of unique ${columnTitle.toLowerCase()} categories`,
        category: 'distribution',
        priority: 75,
        color: '#ef4444'
      });

      // Most common category
      const entries = Object.entries(counts).sort(([,a], [,b]) => (b as number) - (a as number));
      if (entries.length > 0) {
        const [topCategory, topCount] = entries[0];
        const percentage = ((topCount as number) / this.data.length) * 100;

        kpis.push({
          id: `top-${primaryCategoricalColumn}`,
          title: `Top ${columnTitle}`,
          value: topCategory,
          formattedValue: `${topCategory} (${percentage.toFixed(1)}%)`,
          type: 'percentage',
          icon: '🥇',
          description: `Most common ${columnTitle.toLowerCase()} category`,
          category: 'performance',
          priority: 85,
          color: '#10b981'
        });
      }
    }

    return kpis;
  }

  private generateTimeBasedKPIs(): KPI[] {
    const kpis: KPI[] = [];
    const dateColumns = this.getDateColumns();

    if (dateColumns.length === 0) return kpis;

    const primaryDateColumn = dateColumns[0];
    const dates = this.data
      .map(row => new Date(row[primaryDateColumn]))
      .filter(date => !isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    if (dates.length > 1) {
      const earliestDate = dates[0];
      const latestDate = dates[dates.length - 1];
      const daysDiff = Math.ceil((latestDate.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24));

      // Time span
      kpis.push({
        id: 'time-span',
        title: 'Data Time Span',
        value: daysDiff,
        formattedValue: this.formatTimePeriod(daysDiff),
        type: 'range',
        icon: '⏰',
        description: 'Time period covered by the dataset',
        category: 'volume',
        priority: 70,
        color: '#8b5cf6'
      });

      // Data frequency
      const avgDaysBetweenRecords = daysDiff / dates.length;
      let frequency = 'Daily';
      if (avgDaysBetweenRecords > 25) frequency = 'Monthly';
      else if (avgDaysBetweenRecords > 6) frequency = 'Weekly';

      kpis.push({
        id: 'data-frequency',
        title: 'Data Frequency',
        value: frequency,
        formattedValue: `~${frequency}`,
        type: 'ratio',
        icon: '📅',
        description: 'Estimated frequency of data collection',
        category: 'quality',
        priority: 65,
        color: '#06b6d4'
      });
    }

    return kpis;
  }

  private generateQualityKPIs(): KPI[] {
    const kpis: KPI[] = [];

    // Only include data quality if it's significantly poor (below 85%)
    // Most business users don't need to worry about perfect data quality
    const totalCells = this.data.length * this.headers.length;
    const emptyCells = this.data.reduce((count, row) => {
      return count + this.headers.filter(header => 
        row[header] === '' || row[header] == null || row[header] === undefined
      ).length;
    }, 0);
    
    const completeness = ((totalCells - emptyCells) / totalCells) * 100;

    // Only show data quality KPI if it's a concern (below 85%) or excellent (above 98%)
    if (completeness < 85 || completeness > 98) {
      kpis.push({
        id: 'data-completeness',
        title: completeness < 85 ? 'Data Issues' : 'Data Quality',
        value: completeness,
        formattedValue: completeness < 85 ? 
          `${(100 - completeness).toFixed(1)}% missing` : 
          `${completeness.toFixed(1)}% complete`,
        type: 'percentage',
        trend: completeness > 95 ? 'up' : completeness > 80 ? 'stable' : 'down',
        icon: completeness > 95 ? '✅' : completeness > 80 ? '⚠️' : '❌',
        description: completeness < 85 ? 
          'Some data is missing - results may be incomplete' : 
          'High quality dataset with complete information',
        category: 'quality',
        priority: completeness < 85 ? 90 : 60, // Higher priority if there are issues
        color: completeness > 95 ? '#10b981' : completeness > 80 ? '#f59e0b' : '#ef4444'
      });
    }

    return kpis;
  }

  // Utility methods
  private getNumericColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'numeric')
      .map(([name]) => name);
  }

  private getCategoricalColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'categorical')
      .map(([name]) => name);
  }

  private getDateColumns(): string[] {
    return Array.from(this.columnInfo.entries())
      .filter(([, info]) => info.type === 'datetime')
      .map(([name]) => name);
  }

  private getNumericValues(column: string): number[] {
    return this.data
      .map(row => Number(row[column]))
      .filter(val => !isNaN(val));
  }

  private getCategoryCounts(column: string): Record<string, number> {
    return this.data.reduce((acc, row) => {
      const value = row[column];
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  private findPrimaryNumericColumn(columns: string[]): string | null {
    if (columns.length === 0) return null;

    // Prioritize by business relevance keywords
    const businessKeywords = [
      'revenue', 'sales', 'profit', 'cost', 'price', 'amount', 'value', 'total',
      'count', 'quantity', 'volume', 'score', 'rating', 'performance'
    ];

    for (const keyword of businessKeywords) {
      const match = columns.find(col => 
        col.toLowerCase().includes(keyword)
      );
      if (match) return match;
    }

    // Fallback to column with highest variance
    let maxVariance = 0;
    let primaryColumn = columns[0];

    columns.forEach(col => {
      const values = this.getNumericValues(col);
      if (values.length > 1) {
        const variance = this.calculateVariance(values);
        if (variance > maxVariance) {
          maxVariance = variance;
          primaryColumn = col;
        }
      }
    });

    return primaryColumn;
  }

  private findPrimaryCategoricalColumn(columns: string[]): string | null {
    if (columns.length === 0) return null;

    // Prioritize by business relevance
    const businessKeywords = [
      'category', 'type', 'status', 'region', 'department', 'product', 'customer',
      'segment', 'group', 'class', 'brand', 'model'
    ];

    for (const keyword of businessKeywords) {
      const match = columns.find(col => 
        col.toLowerCase().includes(keyword)
      );
      if (match) return match;
    }

    // Fallback to most diverse column (optimal number of categories)
    return columns.reduce((best, current) => {
      const currentInfo = this.columnInfo.get(current);
      const bestInfo = this.columnInfo.get(best);
      
      if (!currentInfo || !bestInfo) return best;
      
      // Prefer columns with 3-20 unique values (good for analysis)
      const currentScore = this.getCategoryDiversityScore(currentInfo.uniqueValues);
      const bestScore = this.getCategoryDiversityScore(bestInfo.uniqueValues);
      
      return currentScore > bestScore ? current : best;
    });
  }

  private getCategoryDiversityScore(uniqueValues: number): number {
    // Optimal range is 3-20 categories
    if (uniqueValues < 2) return 0;
    if (uniqueValues <= 10) return uniqueValues * 10;
    if (uniqueValues <= 20) return 100 - (uniqueValues - 10) * 2;
    return Math.max(0, 50 - uniqueValues);
  }

  private calculateVariance(values: number[]): number {
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    return values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  }

  private formatColumnName(column: string): string {
    return column
      .replace(/[_-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private getNumericIcon(column: string): string {
    const lowerCol = column.toLowerCase();
    if (lowerCol.includes('revenue') || lowerCol.includes('sales') || lowerCol.includes('income')) return '💰';
    if (lowerCol.includes('cost') || lowerCol.includes('expense') || lowerCol.includes('spend')) return '💸';
    if (lowerCol.includes('profit') || lowerCol.includes('margin')) return '📈';
    if (lowerCol.includes('price') || lowerCol.includes('amount') || lowerCol.includes('value')) return '💵';
    if (lowerCol.includes('count') || lowerCol.includes('quantity') || lowerCol.includes('volume')) return '🔢';
    if (lowerCol.includes('score') || lowerCol.includes('rating') || lowerCol.includes('performance')) return '⭐';
    if (lowerCol.includes('growth') || lowerCol.includes('rate')) return '📊';
    return '📊';
  }

  private getNumericCategory(column: string): KPI['category'] {
    const lowerCol = column.toLowerCase();
    if (lowerCol.includes('revenue') || lowerCol.includes('sales') || lowerCol.includes('profit') || 
        lowerCol.includes('cost') || lowerCol.includes('price')) return 'financial';
    if (lowerCol.includes('score') || lowerCol.includes('rating') || lowerCol.includes('performance')) return 'performance';
    if (lowerCol.includes('count') || lowerCol.includes('quantity') || lowerCol.includes('volume')) return 'volume';
    return 'performance';
  }

  private formatNumber(num: number): string {
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    if (num % 1 === 0) return num.toString();
    return num.toFixed(2);
  }

  private formatTimePeriod(days: number): string {
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.round(days / 30)} months`;
    return `${(days / 365).toFixed(1)} years`;
  }
}